import {
  AppstoreOutlined,
  EnvironmentOutlined,
  LockOutlined,
  LoginOutlined,
  LogoutOutlined,
  MenuOutlined,
  ShoppingCartOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  Badge,
  Button,
  Drawer,
  Dropdown,
  Layout,
  Menu,
  Space,
  theme,
  Typography,
  type MenuProps,
} from "antd";
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { ACCOUNT_NAV_ITEMS, ROUTES } from "@/constants";
import { useAuth, useCart, useResponsive } from "@/hooks";
import { confirmLogout } from "@/utils/auth-confirm";

const { Header: AntHeader } = Layout;
const { Text } = Typography;

const navigationItems = [
  { key: ROUTES.HOME, label: <Link to={ROUTES.HOME}>Trang chủ</Link> },
  {
    key: ROUTES.PRODUCTS,
    label: <Link to={ROUTES.PRODUCTS}>Sản phẩm</Link>,
  },
  {
    key: ROUTES.GOLD_PRICE,
    label: <Link to={ROUTES.GOLD_PRICE}>Giá vàng</Link>,
  },
  {
    key: ROUTES.NEWS,
    label: <Link to={ROUTES.NEWS}>Tin tức</Link>,
  },
  {
    key: ROUTES.CHECKOUT,
    label: <Link to={ROUTES.CHECKOUT}>Thanh toán</Link>,
  },
];

const ACCOUNT_MENU_ICONS = {
  profile: <UserOutlined />,
  addresses: <EnvironmentOutlined />,
  orders: <ShoppingCartOutlined />,
  security: <LockOutlined />,
} as const;

export const Header = () => {
  const { token } = theme.useToken();
  const { isCompactHeader, isDesktop, isMobile } = useResponsive();
  const [isNavDrawerOpen, setIsNavDrawerOpen] = useState(false);
  const [isDesktopAccountMenuOpen, setIsDesktopAccountMenuOpen] =
    useState(false);
  const [isMobileAccountMenuOpen, setIsMobileAccountMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { badgeCount, openDrawer } = useCart();
  const { isAuthenticated, logout, user } = useAuth();

  const selectedKey =
    location.pathname === ROUTES.HOME
      ? ROUTES.HOME
      : (navigationItems.find(
          (item) =>
            item.key !== ROUTES.HOME && location.pathname.startsWith(item.key),
        )?.key ?? ROUTES.HOME);

  const closeDrawer = () => setIsNavDrawerOpen(false);
  const closeAccountMenus = () => {
    setIsDesktopAccountMenuOpen(false);
    setIsMobileAccountMenuOpen(false);
  };
  const handleLogout = () => {
    closeAccountMenus();
    confirmLogout(() => logout());
  };
  const handleAccountButtonClick = () => {
    closeAccountMenus();
    navigate(ROUTES.ACCOUNT_PROFILE);
  };
  const handleAccountMenuClick: MenuProps["onClick"] = ({ key }) => {
    const selectedKey = String(key);

    if (selectedKey === "logout") {
      handleLogout();
      return;
    }

    closeAccountMenus();
    navigate(selectedKey);
  };
  const accountMenuItems: MenuProps["items"] = [
    ...ACCOUNT_NAV_ITEMS.map((item) => ({
      key: item.path,
      icon: ACCOUNT_MENU_ICONS[item.key],
      label: item.label,
    })),
    {
      type: "divider",
    },
    {
      key: "logout",
      danger: true,
      icon: <LogoutOutlined />,
      label: "Đăng xuất",
    },
  ];

  return (
    <AntHeader
      style={{
        position: "sticky",
        top: 0,
        zIndex: 100,
        height: "auto",
        padding: 0,
        background: token.colorBgContainer,
        borderBottom: `1px solid ${token.colorBorderSecondary}`,
      }}
    >
      <div className="header-shell">
        <div className="header-shell__brand">
          <Link to={ROUTES.HOME}>
            <Text className="header-shell__logo">ERP GOLD</Text>
          </Link>
          <Text type="secondary" className="header-shell__tagline">
            Storefront cho khách hàng
          </Text>
        </div>

        {!isCompactHeader ? (
          <Menu
            mode="horizontal"
            selectedKeys={[selectedKey]}
            items={navigationItems}
            className="header-shell__nav"
          />
        ) : null}

        <Space className="header-shell__actions" size={isMobile ? 8 : 12} wrap>
          <Badge count={badgeCount} color={token.colorPrimary}>
            <Button
              aria-label="Mở giỏ hàng"
              icon={<ShoppingCartOutlined />}
              onClick={openDrawer}
            >
              {isMobile ? null : "Giỏ hàng"}
            </Button>
          </Badge>

          {isAuthenticated ? (
            isDesktop ? (
              <Dropdown
                menu={{
                  items: accountMenuItems,
                  onClick: handleAccountMenuClick,
                }}
                mouseLeaveDelay={0.12}
                onOpenChange={setIsDesktopAccountMenuOpen}
                open={isDesktopAccountMenuOpen}
                placement="bottomRight"
                trigger={["hover"]}
              >
                <Button
                  aria-label="Đi tới trang tài khoản"
                  className="header-shell__account-button"
                  icon={<UserOutlined />}
                  onClick={handleAccountButtonClick}
                >
                  {isMobile ? null : user?.name || "Khách hàng"}
                </Button>
              </Dropdown>
            ) : (
              <Dropdown
                menu={{
                  items: accountMenuItems,
                  onClick: handleAccountMenuClick,
                }}
                onOpenChange={setIsMobileAccountMenuOpen}
                open={isMobileAccountMenuOpen}
                placement="bottomRight"
                trigger={["click"]}
              >
                <Button aria-label="Mở tài khoản" icon={<UserOutlined />}>
                  {isMobile ? null : user?.name || "Khách hàng"}
                </Button>
              </Dropdown>
            )
          ) : (
            <Link to={ROUTES.LOGIN}>
              <Button
                aria-label="Đăng nhập"
                type="primary"
                icon={<LoginOutlined />}
              >
                {isMobile ? null : "Đăng nhập"}
              </Button>
            </Link>
          )}

          {isCompactHeader ? (
            <Button
              icon={<MenuOutlined />}
              aria-label="Mở menu điều hướng"
              onClick={() => setIsNavDrawerOpen(true)}
            />
          ) : null}
        </Space>
      </div>

      <Drawer
        title="Điều hướng"
        placement="right"
        open={isNavDrawerOpen}
        onClose={closeDrawer}
        width={isMobile ? "88vw" : 360}
      >
        <Menu
          mode="vertical"
          selectedKeys={[selectedKey]}
          onClick={closeDrawer}
          items={navigationItems.map((item) => ({
            key: item.key,
            label: item.label,
            icon: <AppstoreOutlined />,
          }))}
        />
      </Drawer>
    </AntHeader>
  );
};
